import { html, SEVERITY_SORT_ASC, SEVERITY_SORT_DESC } from './util.js';

export function exportHTML({ logs, threats, enrichments, imports }) {
  const threatCount = threats.length;
  const logCount = logs.length;
  const severityCounts = { low: 0, medium: 0, high: 0, critical: 0 };
  const statusCounts = { investigating: 0, active: 0, resolved: 0 };

  threats.forEach(t => {
    severityCounts[t.severity] = (severityCounts[t.severity] || 0) + 1;
    statusCounts[t.status] = (statusCounts[t.status] || 0) + 1;
  });

  // ── Login result breakdown ──
  let successCount = 0;
  let failCount = 0;
  const uniqueIps = new Set();
  const uniqueUsers = new Set();
  for (const log of logs) {
    if (log.result === 'Success') successCount++;
    else failCount++;
    if (log.ip) uniqueIps.add(log.ip);
    if (log.user) uniqueUsers.add(log.user);
  }

  // ── User stats (most targeted) ──
  const userMap = {};
  for (const log of logs) {
    if (!log.user) continue;
    if (!userMap[log.user]) {
      userMap[log.user] = { user: log.user, failed: 0, success: 0, total: 0 };
    }
    if (log.result === 'Success') {
      userMap[log.user].success++;
    } else {
      userMap[log.user].failed++;
    }
    userMap[log.user].total++;
  }
  const topUsers = Object.values(userMap)
    .sort((a, b) => b.failed - a.failed)
    .slice(0, 20);

  // ── IP stats (top failed IPs) ──
  const ipMap = {};
  for (const log of logs) {
    if (!log.ip) continue;
    if (log.result === 'Success') continue;
    if (!ipMap[log.ip]) {
      ipMap[log.ip] = { ip: log.ip, failed: 0 };
    }
    ipMap[log.ip].failed++;
  }
  const topIps = Object.values(ipMap)
    .sort((a, b) => b.failed - a.failed)
    .slice(0, 20);

  const enrichMap = {};
  enrichments.forEach(e => { enrichMap[e.ip] = e; });

  // ── ASN stats (top failed ASNs) ──
  const ipFailCount = {};
  for (const log of logs) {
    if (!log.ip || log.result === 'Success') continue;
    ipFailCount[log.ip] = (ipFailCount[log.ip] || 0) + 1;
  }
  const asnMap = {};
  for (const [ip, count] of Object.entries(ipFailCount)) {
    const enrich = enrichMap[ip];
    const asn = enrich?.asn || 'Unknown';
    const asnName = enrich?.asnName || 'Unknown';
    const key = asn;
    if (!asnMap[key]) {
      asnMap[key] = { asn, asnName, failed: 0 };
    }
    asnMap[key].failed += count;
  }
  const topAsns = Object.values(asnMap)
    .sort((a, b) => b.failed - a.failed)
    .slice(0, 20);

  // ── Top threats ──
  const topThreats = threats
    .sort((a, b) => (SEVERITY_SORT_ASC[a.severity] ?? 99) - (SEVERITY_SORT_ASC[b.severity] ?? 99))
    .slice(0, 50);

  const reportHtml = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>ThreatScope Report - ${new Date().toISOString().slice(0, 10)}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif; background: #0f1117; color: #e4e6f0; padding: 24px; line-height: 1.5; }
    h1 { font-size: 1.5rem; margin-bottom: 4px; color: #4f8cff; }
    h2 { font-size: 1.1rem; margin: 20px 0 8px; }
    .meta { color: #6b6e85; font-size: 0.85rem; margin-bottom: 20px; }
    .stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); gap: 12px; margin-bottom: 20px; }
    .stat { background: #1e2130; border: 1px solid #2e3245; border-radius: 8px; padding: 12px 16px; }
    .stat .value { font-size: 1.3rem; font-weight: 700; }
    .stat .label { font-size: 0.75rem; color: #6b6e85; margin-top: 2px; }
    table { width: 100%; border-collapse: collapse; font-size: 0.8rem; margin-top: 8px; }
    th { text-align: left; padding: 8px; background: #1a1d27; color: #9699b4; border-bottom: 1px solid #2e3245; font-weight: 600; }
    td { padding: 6px 8px; border-bottom: 1px solid #2e3245; }
    .severity-critical { color: #ef4444; }
    .severity-high { color: #fb923c; }
    .severity-medium { color: #fbbf24; }
    .severity-low { color: #34d399; }
    .status-investigating { color: #fbbf24; }
    .status-active { color: #ef4444; }
    .status-resolved { color: #34d399; }
    .success { color: #34d399; }
    .failure { color: #ef4444; }
    .section-title { font-size: 1rem; font-weight: 700; margin: 24px 0 8px; padding-bottom: 4px; border-bottom: 1px solid #2e3245; }
    .sub-section-title { font-size: 0.9rem; font-weight: 600; margin: 18px 0 6px; color: #e4e6f0; }
    .indicator-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 10px; margin-bottom: 18px; }
    .indicator-card { background: #1e2130; border: 1px solid #2e3245; border-radius: 8px; padding: 10px 14px; }
    .indicator-card .card-title { font-size: 0.75rem; color: #6b6e85; text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 2px; }
    .indicator-card .card-value { font-size: 1.1rem; font-weight: 700; }
    .indicator-card .card-bar { height: 3px; background: #2e3245; border-radius: 2px; margin-top: 6px; overflow: hidden; }
    .indicator-card .card-bar-fill { height: 100%; border-radius: 2px; background: #ef4444; }
    .indicator-card .card-meta { font-size: 0.7rem; color: #6b6e85; margin-top: 2px; }
    .small-table { width: 100%; border-collapse: collapse; font-size: 0.78rem; margin-top: 6px; }
    .small-table th { text-align: left; padding: 5px 8px; background: #1a1d27; color: #9699b4; border-bottom: 1px solid #2e3245; font-weight: 600; font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.03em; }
    .small-table td { padding: 4px 8px; border-bottom: 1px solid #2e3245; }
    .small-table tr:last-child td { border-bottom: none; }
    .small-table .num { text-align: right; font-variant-numeric: tabular-nums; font-weight: 700; }
    .small-table .fail { color: #ef4444; }
    .small-table .pass { color: #34d399; }
    .small-table .bar-cell { width: 80px; }
    .small-table .bar-bg { height: 3px; background: #2e3245; border-radius: 2px; overflow: hidden; }
    .small-table .bar-fill { height: 100%; border-radius: 2px; background: #ef4444; }
    .footer { margin-top: 24px; padding-top: 12px; border-top: 1px solid #2e3245; font-size: 0.75rem; color: #6b6e85; }
    .map-wrap { margin: 20px 0; }
    .map-wrap svg { width: 100%; max-width: 800px; height: auto; display: block; margin: 0 auto; border-radius: 8px; }
    .map-legend { display: flex; gap: 18px; justify-content: center; margin-top: 10px; font-size: 0.78rem; color: #6b6e85; flex-wrap: wrap; }
    .map-legend-item { display: flex; align-items: center; gap: 6px; }
    .map-legend-dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }
  </style>
</head>
<body>
  <h1>🛡️ ThreatScope Report</h1>
  <p class="meta">Generated ${new Date().toLocaleString()} | ${logCount} log entries | ${threatCount} threats detected</p>

  <div class="stats">
    <div class="stat"><div class="value">${logCount}</div><div class="label">Total Log Entries</div></div>
    <div class="stat"><div class="value success">${successCount}</div><div class="label">Successful</div></div>
    <div class="stat"><div class="value failure">${failCount}</div><div class="label">Failed</div></div>
    <div class="stat"><div class="value">${uniqueUsers.size}</div><div class="label">Unique Users</div></div>
    <div class="stat"><div class="value">${uniqueIps.size}</div><div class="label">Unique IPs</div></div>
    <div class="stat"><div class="value">${threatCount}</div><div class="label">Total Threats</div></div>
    <div class="stat"><div class="value severity-critical">${severityCounts.critical}</div><div class="label">Critical</div></div>
    <div class="stat"><div class="value severity-high">${severityCounts.high}</div><div class="label">High</div></div>
    <div class="stat"><div class="value severity-medium">${severityCounts.medium}</div><div class="label">Medium</div></div>
    <div class="stat"><div class="value severity-low">${severityCounts.low}</div><div class="label">Low</div></div>
    <div class="stat"><div class="value">${imports.length}</div><div class="label">Imports</div></div>
  </div>

  <h2>Geographic Distribution</h2>
  ${generateMapSVG(enrichments, threats, logs)}

  <h2 class="section-title">Key Indicators</h2>

  <div class="indicator-grid">
    <div class="indicator-card">
      <div class="card-title">Most Targeted User</div>
      <div class="card-value">${topUsers.length > 0 ? html(topUsers[0].user) : 'N/A'}</div>
      <div class="card-bar"><div class="card-bar-fill" style="width:100%"></div></div>
      <div class="card-meta">${topUsers.length > 0 ? topUsers[0].failed + ' failed logins' : ''}</div>
    </div>
    <div class="indicator-card">
      <div class="card-title">Top Failed IP</div>
      <div class="card-value">${topIps.length > 0 ? html(topIps[0].ip) : 'N/A'}</div>
      <div class="card-bar"><div class="card-bar-fill" style="width:100%"></div></div>
      <div class="card-meta">${topIps.length > 0 ? topIps[0].failed + ' failed logins' : ''}</div>
    </div>
    <div class="indicator-card">
      <div class="card-title">Top Failed ASN</div>
      <div class="card-value">${topAsns.length > 0 ? html(topAsns[0].asnName) : 'N/A'}</div>
      <div class="card-bar"><div class="card-bar-fill" style="width:100%"></div></div>
      <div class="card-meta">${topAsns.length > 0 ? topAsns[0].failed + ' failed logins' : ''}</div>
    </div>
    <div class="indicator-card">
      <div class="card-title">Failed vs Success Rate</div>
      <div class="card-value">${logCount > 0 ? Math.round((failCount / logCount) * 100) : 0}%</div>
      <div class="card-bar"><div class="card-bar-fill" style="width:${logCount > 0 ? (failCount / logCount) * 100 : 0}%"></div></div>
      <div class="card-meta">${failCount} failed out of ${logCount} total</div>
    </div>
  </div>

  <h3 class="sub-section-title">Most Targeted Users</h3>
  ${topUsers.length > 0 ? `
  <table class="small-table">
    <thead>
      <tr><th>User</th><th>Failed</th><th>Successful</th><th>Total</th><th class="bar-cell"></th></tr>
    </thead>
    <tbody>
      ${topUsers.map((u, i) => {
        const pct = topUsers[0].failed > 0 ? (u.failed / topUsers[0].failed) * 100 : 0;
        return `<tr>
          <td>${html(u.user)}</td>
          <td class="num fail">${u.failed}</td>
          <td class="num pass">${u.success}</td>
          <td class="num">${u.total}</td>
          <td class="bar-cell"><div class="bar-bg"><div class="bar-fill" style="width:${pct}%"></div></div></td>
        </tr>`;
      }).join('')}
    </tbody>
  </table>` : '<p style="color: #6b6e85; margin-top: 6px;">No user login data available.</p>'}

  <h3 class="sub-section-title">Top Failed IPs</h3>
  ${topIps.length > 0 ? `
  <table class="small-table">
    <thead>
      <tr><th>IP Address</th><th>Failed Logins</th><th class="bar-cell"></th></tr>
    </thead>
    <tbody>
      ${topIps.map((s, i) => {
        const pct = topIps[0].failed > 0 ? (s.failed / topIps[0].failed) * 100 : 0;
        return `<tr>
          <td style="font-family:var(--font-mono,'JetBrains Mono',monospace)">${html(s.ip)}</td>
          <td class="num fail">${s.failed}</td>
          <td class="bar-cell"><div class="bar-bg"><div class="bar-fill" style="width:${pct}%"></div></div></td>
        </tr>`;
      }).join('')}
    </tbody>
  </table>` : '<p style="color: #6b6e85; margin-top: 6px;">No failed login data available.</p>'}

  <h3 class="sub-section-title">Top Failed ASNs</h3>
  ${topAsns.length > 0 ? `
  <table class="small-table">
    <thead>
      <tr><th>ASN</th><th>Name</th><th>Failed Logins</th><th class="bar-cell"></th></tr>
    </thead>
    <tbody>
      ${topAsns.map((s, i) => {
        const pct = topAsns[0].failed > 0 ? (s.failed / topAsns[0].failed) * 100 : 0;
        const label = s.asnName && s.asnName !== 'Unknown' ? `${html(s.asnName)} (${html(s.asn)})` : html(s.asn);
        return `<tr>
          <td style="font-family:var(--font-mono,'JetBrains Mono',monospace)">${html(s.asn)}</td>
          <td>${html(s.asnName)}</td>
          <td class="num fail">${s.failed}</td>
          <td class="bar-cell"><div class="bar-bg"><div class="bar-fill" style="width:${pct}%"></div></div></td>
        </tr>`;
      }).join('')}
    </tbody>
  </table>` : '<p style="color: #6b6e85; margin-top: 6px;">No ASN data available — enrich IPs with GeoIP.</p>'}

  <h2>Top Threats</h2>
  <table>
    <thead>
      <tr><th>Severity</th><th>Type</th><th>IP</th><th>User</th><th>Country</th><th>Status</th><th>Timestamp</th></tr>
    </thead>
    <tbody>
      ${topThreats.map(t => {
        const enrich = enrichMap[t.ip];
        const country = enrich ? enrich.country : 'Unknown';
        return `<tr>
          <td class="severity-${t.severity}">${t.severity.toUpperCase()}</td>
          <td>${t.type.replace(/_/g, ' ')}</td>
          <td>${t.ip}</td>
          <td>${html(t.user)}</td>
          <td>${html(country)}</td>
          <td class="status-${t.status}">${t.status}</td>
          <td>${t.timestamp ? new Date(t.timestamp).toLocaleString() : ''}</td>
        </tr>`;
      }).join('')}
    </tbody>
  </table>

  ${threatCount === 0 ? '<p style="color: #6b6e85; margin-top: 12px;">No threats detected.</p>' : ''}

  <div class="footer">
    <p>ThreatScope — Sign-In Log Threat Analysis Tool</p>
    <p>Report generated by ThreatScope v1.0.0</p>
  </div>
</body>
</html>`;

  return reportHtml;
}

export function exportCSV({ logs, threats, enrichments }) {
  const enrichMap = {};
  enrichments.forEach(e => { enrichMap[e.ip] = e; });

  const threatMap = {};
  threats.forEach(t => {
    if (!threatMap[t.logId]) threatMap[t.logId] = [];
    threatMap[t.logId].push(t);
  });

  const headers = [
    'Timestamp', 'User', 'IP', 'App', 'Result', 'Risk',
    'Country', 'Country Code', 'City', 'Region', 'Latitude', 'Longitude',
    'ISP', 'Organization', 'ASN', 'ASN Name',
    'Geo Source',
    'Threat Types', 'Threat Severity', 'Threat Status',
    'AbuseIPDB Score', 'AlienVault Pulses',
    'Source', 'Import ID',
  ];

  const rows = logs.map(log => {
    const enrich = enrichMap[log.ip];
    const logThreats = threatMap[log.id] || [];
    const threatTypes = logThreats.map(t => t.type).join('; ');
    const maxSeverity = logThreats.reduce((max, t) => {
      return (SEVERITY_SORT_DESC[t.severity] ?? 0) > (SEVERITY_SORT_DESC[max] ?? 0) ? t.severity : max;
    }, '');
    const threatStatus = logThreats.map(t => t.status).filter((v, i, a) => a.indexOf(v) === i).join('; ');

    const geoSource = enrich?.fromWhois ? 'WHOIS' : enrich?.fromShodan ? 'Shodan' : enrich?.lat ? 'GEOIP' : '';

    return [
      log.timestamp || '',
      log.user || '',
      log.ip || '',
      log.app || '',
      log.result || '',
      log.risk || '',
      enrich?.country || '',
      enrich?.countryCode || '',
      enrich?.city || '',
      enrich?.region || '',
      enrich?.lat ?? '',
      enrich?.lon ?? '',
      enrich?.isp || '',
      enrich?.org || '',
      enrich?.asn || '',
      enrich?.asnName || '',
      geoSource,
      threatTypes,
      maxSeverity,
      threatStatus,
      enrich?.threatIntel?.abuseIPDB?.abuseConfidenceScore || '',
      enrich?.threatIntel?.alienVault?.pulseCount || '',
      log.source || '',
      log.importId || '',
    ];
  });

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => {
      const s = String(cell);
      if (s.includes(',') || s.includes('"') || s.includes('\n')) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    }).join(',')),
  ].join('\n');

  return csvContent;
}

export function downloadFile(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function generateMapSVG(enrichments, threats, logs) {
  const W = 800;
  const H = 400;
  const toX = lon => (lon + 180) / 360 * W;
  const toY = lat => (90 - lat) / 180 * H;

  const enrichMap = {};
  enrichments.forEach(e => { enrichMap[e.ip] = e; });

  const threatMap = {};
  threats.forEach(t => {
    if (!threatMap[t.logId]) threatMap[t.logId] = { maxSeverity: 'low', types: [] };
    if (SEVERITY_SORT_DESC[t.severity] > SEVERITY_SORT_DESC[threatMap[t.logId].maxSeverity]) {
      threatMap[t.logId].maxSeverity = t.severity;
    }
    threatMap[t.logId].types.push(t.type);
  });

  const pinCounts = {};
  for (const log of logs) {
    const enrich = enrichMap[log.ip];
    if (!enrich || !enrich.lat || !enrich.lon) continue;
    const key = `${enrich.lat},${enrich.lon}`;
    if (!pinCounts[key]) {
      pinCounts[key] = { lat: parseFloat(enrich.lat), lon: parseFloat(enrich.lon), count: 0, threats: [] };
    }
    pinCounts[key].count++;
    const threat = threatMap[log.id];
    if (threat) pinCounts[key].threats.push(threat);
  }

  const pins = Object.values(pinCounts);

  let grid = '';
  for (let lon = -180; lon <= 180; lon += 30) {
    grid += `<line x1="${toX(lon).toFixed(1)}" y1="0" x2="${toX(lon).toFixed(1)}" y2="${H}" stroke="#2e3245" stroke-width="0.5"/>`;
  }
  for (let lat = -90; lat <= 90; lat += 30) {
    grid += `<line x1="0" y1="${toY(lat).toFixed(1)}" x2="${W}" y2="${toY(lat).toFixed(1)}" stroke="#2e3245" stroke-width="0.5"/>`;
  }

  let markers = '';
  for (const pin of pins) {
    const severity = pin.threats.reduce((max, t) => {
      return SEVERITY_SORT_DESC[t.maxSeverity] > SEVERITY_SORT_DESC[max] ? t.maxSeverity : max;
    }, 'none');

    const color = severity === 'critical' ? '#ef4444'
      : severity === 'high' ? '#fb923c'
      : severity === 'medium' ? '#fbbf24'
      : severity === 'low' ? '#34d399'
      : '#4f8cff';

    const r = Math.min(4 + Math.log2(pin.count) * 2, 12);
    markers += `<circle cx="${toX(pin.lon).toFixed(1)}" cy="${toY(pin.lat).toFixed(1)}" r="${r}" fill="${color}" fill-opacity="0.7" stroke="#ffffff" stroke-width="1"/>`;
  }

  const hasData = pins.length > 0;

  return `<div class="map-wrap">
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}">
  <rect width="${W}" height="${H}" fill="#0f1117" rx="8"/>
  ${grid}
  ${hasData ? markers : ''}
</svg>
<div class="map-legend">
  <span class="map-legend-item"><span class="map-legend-dot" style="background:#4f8cff"></span>Normal login</span>
  <span class="map-legend-item"><span class="map-legend-dot" style="background:#34d399"></span>Low threat</span>
  <span class="map-legend-item"><span class="map-legend-dot" style="background:#fbbf24"></span>Medium threat</span>
  <span class="map-legend-item"><span class="map-legend-dot" style="background:#ef4444"></span>High/Critical threat</span>
</div>
${!hasData ? '<p style="color:#6b6e85;font-size:0.85rem;text-align:center;margin-top:8px;">No geolocation data available for map.</p>' : ''}
</div>`;
}
